// Bunny.net Stream Configuration
// Documentation: https://docs.bunny.net/docs/stream-api-overview
// TUS Resumable Uploads: https://docs.bunny.net/stream/tus-resumable-uploads

import * as tus from 'tus-js-client';
import SHA256 from 'crypto-js/sha256';

// Default configuration (can be overridden by environment variables)
const BUNNY_LIBRARY_ID = '645326';
const BUNNY_API_KEY = 'f202436c-cf90-4ce8-a255213b688c-c8a6-4d47';
const BUNNY_CDN_HOSTNAME = 'vz-59047644-a93.b-cdn.net';

// Storage Zone configuration (for non-video files like images, documents)
const BUNNY_STORAGE_ZONE = 'zktutorials';
const BUNNY_STORAGE_PASSWORD = 'd422a41a-a421-4411-992461794f24-b7eb-4ba5';
const BUNNY_STORAGE_HOSTNAME = 'storage.bunnycdn.com';

// TUS upload endpoint for Bunny Stream
const BUNNY_TUS_ENDPOINT = 'https://video.bunnycdn.com/tusupload';

// Track active TUS uploads for abort/cancel support
const activeTusUploads = new Map<string, tus.Upload>();

export const BUNNY_CONFIG = {
  LIBRARY_ID: process.env.REACT_APP_BUNNY_LIBRARY_ID || BUNNY_LIBRARY_ID,
  API_KEY: process.env.REACT_APP_BUNNY_API_KEY || BUNNY_API_KEY,
  CDN_HOSTNAME: process.env.REACT_APP_BUNNY_CDN_HOSTNAME || BUNNY_CDN_HOSTNAME,
  STORAGE_ZONE: process.env.REACT_APP_BUNNY_STORAGE_ZONE || BUNNY_STORAGE_ZONE,
  STORAGE_PASSWORD: process.env.REACT_APP_BUNNY_STORAGE_PASSWORD || BUNNY_STORAGE_PASSWORD,
  STORAGE_HOSTNAME: process.env.REACT_APP_BUNNY_STORAGE_HOSTNAME || BUNNY_STORAGE_HOSTNAME,
};

// Helper function to get resource type from file
export const getResourceTypeFromFile = (file: File): 'video' | 'image' | 'raw' => {
  if (file.type.startsWith('video/')) {
    return 'video';
  } else if (file.type.startsWith('image/')) {
    return 'image';
  } else {
    return 'raw';
  }
};

// Helper function to get video duration from file
export const getVideoDuration = async (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    // Create object URL
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.remove();
    };

    video.onloadedmetadata = () => {
      const duration = video.duration || 0;
      console.log('Video duration detected from file:', duration);
      cleanup();
      resolve(duration);
    };

    video.onerror = (e) => {
      console.log('Failed to get video duration from file:', e);
      cleanup();
      resolve(0);
    };

    video.src = objectUrl;

    // Set a timeout in case the video doesn't load (increased for bulk uploads)
    setTimeout(() => {
      if (video.duration === undefined || isNaN(video.duration) || video.duration === 0) {
        console.log('Video duration timeout, using 0');
        cleanup();
        resolve(0);
      }
    }, 15000); // Increased timeout to 15s to handle concurrent IO limits
  });
};

// Generate a unique filename with timestamp
const generateUniqueFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop() || '';
  const baseName = originalName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_');
  return `${baseName}_${timestamp}_${randomStr}.${extension}`;
};

// Interface for video upload response
interface BunnyVideoResponse {
  guid: string;
  title: string;
  dateUploaded: string;
  status: number;
  length: number;
  thumbnailUrl?: string;
}

// Interface for upload result
interface UploadResult {
  url: string;
  publicId: string;
  duration?: number;
  videoId?: string;
  thumbnailUrl?: string;
}

/**
 * Generate SHA256 signature for Bunny TUS authentication
 * Signature = SHA256(libraryId + apiKey + expirationTime + videoId)
 */
const generateTusSignature = (
  libraryId: string,
  apiKey: string,
  expirationTime: number,
  videoId: string
): string => {
  const signatureString = `${libraryId}${apiKey}${expirationTime}${videoId}`;
  return SHA256(signatureString).toString();
};

/**
 * Upload video to Bunny Stream using TUS Resumable Upload Protocol
 * 
 * Benefits over the previous XHR approach:
 * - Resumable: uploads can resume from where they left off after network interruptions
 * - Retry: automatic retry with exponential backoff on transient failures
 * - Chunked: large files are uploaded in 5MB chunks, reducing memory pressure
 * - Reliable: TUS protocol ensures upload integrity with server-side validation
 * 
 * @see https://docs.bunny.net/stream/tus-resumable-uploads
 */
export const uploadVideoToBunnyStream = async (
  file: File,
  onProgress?: (percent: number) => void
): Promise<UploadResult> => {
  if (!file) {
    throw new Error('File is required');
  }

  try {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    console.log(`[TUS] Starting resumable upload to Bunny Stream:`, {
      libraryId: BUNNY_CONFIG.LIBRARY_ID,
      fileName: file.name,
      fileSize: `${fileSizeMB} MB`,
      fileType: file.type,
    });

    // Step 1: Create video entry in Bunny Stream (unchanged from before)
    const videoTitle = file.name.replace(/\.[^/.]+$/, ''); // Remove extension for title

    const createResponse = await fetch(
      `https://video.bunnycdn.com/library/${BUNNY_CONFIG.LIBRARY_ID}/videos`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'AccessKey': BUNNY_CONFIG.API_KEY,
        },
        body: JSON.stringify({
          title: videoTitle,
        }),
      }
    );

    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      console.error('[TUS] Failed to create video entry:', createResponse.status, errorText);
      throw new Error(`Failed to create video: ${createResponse.status} - ${errorText}`);
    }

    const videoData: BunnyVideoResponse = await createResponse.json();
    console.log('[TUS] Video entry created:', videoData);

    // Extract duration from local file before upload starts (most reliable for bulk uploads)
    let duration = 0;
    try {
      duration = await getVideoDuration(file);
    } catch (e) {
      console.log('Could not get video duration from file:', e);
    }
    // Fallback to Bunny metadata if available at creation time
    if (duration === 0 && videoData.length && videoData.length > 0) {
      duration = videoData.length;
    }

    // Step 2: Generate TUS authentication signature
    // Signature expires in 24 hours — plenty of time for large uploads
    const expirationTime = Math.floor(Date.now() / 1000) + 86400; // 24 hours from now
    const signature = generateTusSignature(
      BUNNY_CONFIG.LIBRARY_ID,
      BUNNY_CONFIG.API_KEY,
      expirationTime,
      videoData.guid
    );

    console.log('[TUS] Authentication signature generated, expiry:', new Date(expirationTime * 1000).toISOString());

    // Step 3: Upload using TUS resumable protocol
    await new Promise<void>((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: BUNNY_TUS_ENDPOINT,

        // Retry with exponential backoff: immediate, 3s, 5s, 10s, 20s, 60s, 60s
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],

        // 5 MB chunk size — good balance for large files on variable connections
        chunkSize: 5 * 1024 * 1024,

        // Bunny.net TUS authentication headers
        headers: {
          'AuthorizationSignature': signature,
          'AuthorizationExpire': expirationTime.toString(),
          'VideoId': videoData.guid,
          'LibraryId': BUNNY_CONFIG.LIBRARY_ID,
        },

        // Video metadata for Bunny Stream
        metadata: {
          filetype: file.type || 'video/mp4',
          title: videoTitle,
        },

        // Progress tracking
        onProgress: function (bytesUploaded: number, bytesTotal: number) {
          const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
          console.log(`[TUS] Upload progress: ${percentage}% (${(bytesUploaded / (1024 * 1024)).toFixed(1)}/${(bytesTotal / (1024 * 1024)).toFixed(1)} MB)`);
          if (onProgress) {
            try { onProgress(percentage); } catch (e) { /* ignore callback errors */ }
          }
        },

        // Upload completed successfully
        onSuccess: function () {
          console.log(`[TUS] Upload completed successfully for: ${file.name}`);
          activeTusUploads.delete(videoData.guid);
          resolve();
        },

        // Upload failed after all retries exhausted
        onError: function (error: Error) {
          console.error(`[TUS] Upload failed for ${file.name}:`, error.message);
          activeTusUploads.delete(videoData.guid);
          reject(new Error(`TUS upload failed: ${error.message}`));
        },

        // Decide whether to retry on error — retry on network errors and 5xx server errors
        onShouldRetry: function (err: any, retryAttempt: number, _options: any) {
          const status = err?.originalResponse?.getStatus?.();
          console.warn(`[TUS] Retry attempt ${retryAttempt + 1} for ${file.name}`, {
            status,
            message: err?.message,
          });

          // Retry on server errors (5xx)
          if (status && status >= 500 && status < 600) {
            return true;
          }

          // Retry on network errors (no status = connection lost)
          if (!status) {
            return true;
          }

          // Don't retry on client errors (4xx) except 408 (Request Timeout) and 429 (Too Many Requests)
          if (status === 408 || status === 429) {
            return true;
          }

          // Don't retry on other 4xx errors
          if (status >= 400 && status < 500) {
            return false;
          }

          // Default: retry
          return true;
        },

        // Called before each request — useful for logging
        onBeforeRequest: function (req: any) {
          // Ensure headers are set on every request (including retries)
          const xhr = req.getUnderlyingObject?.();
          if (xhr) {
            console.log(`[TUS] Sending request to: ${req.getURL?.() || 'unknown'}`);
          }
        },

        // Called after upload URL is created — log for debugging
        onAfterResponse: function (_req: any, res: any) {
          const status = res.getStatus?.();
          if (status && status >= 400) {
            console.warn(`[TUS] Server responded with status ${status}`);
          }
        },
      });

      // Track the upload for abort/cancel support
      activeTusUploads.set(videoData.guid, upload);

      // Check for previous uploads to resume (key feature for unreliable networks)
      upload.findPreviousUploads().then(function (previousUploads) {
        if (previousUploads.length > 0) {
          console.log(`[TUS] Found ${previousUploads.length} previous upload(s), resuming from last checkpoint...`);
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        // Start the upload (or resume)
        upload.start();
      }).catch(function (err) {
        console.warn('[TUS] Could not check for previous uploads, starting fresh:', err);
        upload.start();
      });
    });

    // After upload, try Bunny API length if local duration is still zero
    if (duration === 0) {
      try {
        const statusResponse = await fetch(
          `https://video.bunnycdn.com/library/${BUNNY_CONFIG.LIBRARY_ID}/videos/${videoData.guid}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'AccessKey': BUNNY_CONFIG.API_KEY,
            },
          }
        );
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          if (statusData.length && statusData.length > 0) {
            duration = statusData.length;
          }
        }
      } catch (e) {
        console.log('Could not fetch Bunny video length after upload:', e);
      }
    }

    // Construct the playback URL
    // Format for Bunny Stream MP4: https://{CDN_HOSTNAME}/{VIDEO_ID}/{resolution}.mp4
    // Available resolutions: 720.mp4, 480.mp4, 360.mp4, 240.mp4
    // OR use HLS for adaptive streaming: https://{CDN_HOSTNAME}/{VIDEO_ID}/playlist.m3u8
    const playbackUrl = `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoData.guid}/playlist.m3u8`;
    const thumbnailUrl = `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoData.guid}/thumbnail.jpg`;

    console.log('[TUS] Video uploaded successfully:', {
      videoId: videoData.guid,
      playbackUrl,
      thumbnailUrl,
      duration,
    });

    return {
      url: playbackUrl,
      publicId: videoData.guid,
      duration,
      videoId: videoData.guid,
      thumbnailUrl,
    };
  } catch (error) {
    console.error('[TUS] Bunny Stream upload error:', error);
    throw error;
  }
};

/**
 * Abort an active TUS video upload by videoId
 * Useful for cancellation when user navigates away or explicitly cancels
 */
export const abortVideoUpload = (videoId: string): boolean => {
  const upload = activeTusUploads.get(videoId);
  if (upload) {
    upload.abort();
    activeTusUploads.delete(videoId);
    console.log(`[TUS] Upload aborted for video: ${videoId}`);
    return true;
  }
  return false;
};

/**
 * Get the number of currently active TUS uploads
 */
export const getActiveUploadCount = (): number => {
  return activeTusUploads.size;
};

/**
 * Upload file to Bunny Storage (for images and documents)
 * This is for non-video files that don't need transcoding
 */
export const uploadFileToBunnyStorage = async (
  file: File,
  folder: string = 'uploads',
  onProgress?: (percent: number) => void
): Promise<UploadResult> => {
  if (!file) {
    throw new Error('File is required');
  }

  try {
    const uniqueFileName = generateUniqueFileName(file.name);
    const filePath = `${folder}/${uniqueFileName}`;

    console.log('Uploading file to Bunny Storage:', {
      storageZone: BUNNY_CONFIG.STORAGE_ZONE,
      fileName: file.name,
      filePath,
      fileSize: file.size,
    });

    const uploadUrl = `https://${BUNNY_CONFIG.STORAGE_HOSTNAME}/${BUNNY_CONFIG.STORAGE_ZONE}/${filePath}`;

    // Use XMLHttpRequest for progress tracking
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('AccessKey', BUNNY_CONFIG.STORAGE_PASSWORD);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          try { onProgress(percent); } catch (e) { /* ignore callback errors */ }
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status} - ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during Bunny Storage upload'));
      xhr.send(file);
    });

    // Construct the CDN URL for the uploaded file
    // You'll need to create a Pull Zone connected to your storage zone
    // For now, using the storage URL format
    const cdnUrl = `https://${BUNNY_CONFIG.STORAGE_ZONE}.b-cdn.net/${filePath}`;

    console.log('File uploaded to Bunny Storage:', {
      filePath,
      cdnUrl,
    });

    return {
      url: cdnUrl,
      publicId: filePath,
    };
  } catch (error) {
    console.error('Bunny Storage upload error:', error);
    throw error;
  }
};

/**
 * Main upload function that routes to the appropriate upload method
 * This replaces the Cloudinary uploadToCloudinary function
 */
export const uploadToBunny = async (
  file: File,
  resourceType: 'video' | 'image' | 'raw' | 'auto' = 'auto',
  onProgress?: (percent: number) => void
): Promise<UploadResult> => {
  // Auto-detect resource type if 'auto' is specified
  let actualResourceType: 'video' | 'image' | 'raw' = 'raw';
  if (resourceType === 'auto') {
    actualResourceType = getResourceTypeFromFile(file);
  } else {
    actualResourceType = resourceType;
  }

  console.log('Upload to Bunny - detected type:', actualResourceType);

  // Route to appropriate upload method
  if (actualResourceType === 'video') {
    return uploadVideoToBunnyStream(file, onProgress);
  } else {
    // Images and documents go to Bunny Storage
    const folder = actualResourceType === 'image' ? 'images' : 'documents';
    return uploadFileToBunnyStorage(file, folder, onProgress);
  }
};

/**
 * Delete video from Bunny Stream
 */
export const deleteVideoFromBunnyStream = async (videoId: string): Promise<boolean> => {
  if (!videoId) {
    throw new Error('Video ID is required');
  }

  try {
    const response = await fetch(
      `https://video.bunnycdn.com/library/${BUNNY_CONFIG.LIBRARY_ID}/videos/${videoId}`,
      {
        method: 'DELETE',
        headers: {
          'AccessKey': BUNNY_CONFIG.API_KEY,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to delete video:', response.status, errorText);
      return false;
    }

    console.log('Video deleted from Bunny Stream:', videoId);
    return true;
  } catch (error) {
    console.error('Bunny Stream delete error:', error);
    return false;
  }
};

/**
 * Delete file from Bunny Storage
 */
export const deleteFileFromBunnyStorage = async (filePath: string): Promise<boolean> => {
  if (!filePath) {
    throw new Error('File path is required');
  }

  try {
    const deleteUrl = `https://${BUNNY_CONFIG.STORAGE_HOSTNAME}/${BUNNY_CONFIG.STORAGE_ZONE}/${filePath}`;

    const response = await fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'AccessKey': BUNNY_CONFIG.STORAGE_PASSWORD,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to delete file:', response.status, errorText);
      return false;
    }

    console.log('File deleted from Bunny Storage:', filePath);
    return true;
  } catch (error) {
    console.error('Bunny Storage delete error:', error);
    return false;
  }
};

/**
 * Delete file from Bunny (routes to appropriate delete method)
 * This replaces the Cloudinary deleteFromCloudinary function
 */
export const deleteFromBunny = async (
  publicId: string,
  resourceType: 'video' | 'image' | 'raw' = 'raw'
): Promise<boolean> => {
  if (resourceType === 'video') {
    return deleteVideoFromBunnyStream(publicId);
  } else {
    return deleteFileFromBunnyStorage(publicId);
  }
};

/**
 * Get Bunny Stream video URL
 */
export const getBunnyVideoUrl = (
  videoId: string,
  format: 'mp4' | 'hls' | 'thumbnail' | '720p' | '480p' | '360p' | '240p' = 'hls'
): string => {
  if (!videoId) {
    throw new Error('Video ID is required');
  }

  switch (format) {
    case 'hls':
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/playlist.m3u8`;
    case 'thumbnail':
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/thumbnail.jpg`;
    case '720p':
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/720.mp4`;
    case '480p':
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/480.mp4`;
    case '360p':
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/360.mp4`;
    case '240p':
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/240.mp4`;
    case 'mp4':
    default:
      // Default to HLS for best compatibility and adaptive streaming
      return `https://${BUNNY_CONFIG.CDN_HOSTNAME}/${videoId}/playlist.m3u8`;
  }
};

/**
 * Get Bunny Storage file URL
 */
export const getBunnyStorageUrl = (filePath: string): string => {
  if (!filePath) {
    throw new Error('File path is required');
  }
  return `https://${BUNNY_CONFIG.STORAGE_ZONE}.b-cdn.net/${filePath}`;
};

/**
 * Transform Bunny Stream video URL to use correct format
 * Fixes old URLs that used incorrect /play.mp4 format
 */
export const transformBunnyVideoUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  // Check if this is a Bunny Stream URL with the incorrect /play.mp4 format
  if (url.includes('.b-cdn.net') && url.endsWith('/play.mp4')) {
    // Replace /play.mp4 with /playlist.m3u8 for HLS streaming (more compatible)
    return url.replace('/play.mp4', '/playlist.m3u8');
  }

  return url;
};

/**
 * Check if a URL is from Bunny CDN
 */
export const isBunnyUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('.b-cdn.net') || url.includes('bunnycdn.com');
};

/**
 * Validate Bunny URL format
 */
export const validateBunnyUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return isBunnyUrl(url);
};

/**
 * Get video length in seconds from Bunny Stream
 */
export const getBunnyVideoLength = async (videoId: string): Promise<number> => {
  if (!videoId) return 0;

  try {
    const response = await fetch(
      `https://video.bunnycdn.com/library/${BUNNY_CONFIG.LIBRARY_ID}/videos/${videoId}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'AccessKey': BUNNY_CONFIG.API_KEY,
        },
      }
    );

    if (!response.ok) return 0;

    const videoData = await response.json();
    return videoData.length && videoData.length > 0 ? videoData.length : 0;
  } catch (error) {
    console.error('Failed to get Bunny video length:', error);
    return 0;
  }
};

/**
 * Get video status from Bunny Stream
 */
export const getVideoStatus = async (videoId: string): Promise<{
  status: 'processing' | 'ready' | 'failed';
  progress?: number;
}> => {
  if (!videoId) {
    throw new Error('Video ID is required');
  }

  try {
    const response = await fetch(
      `https://video.bunnycdn.com/library/${BUNNY_CONFIG.LIBRARY_ID}/videos/${videoId}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'AccessKey': BUNNY_CONFIG.API_KEY,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get video status: ${response.status}`);
    }

    const videoData = await response.json();

    // Bunny Stream status codes:
    // 0 = Created, 1 = Uploaded, 2 = Processing, 3 = Transcoding, 4 = Finished, 5 = Error
    const statusMap: { [key: number]: 'processing' | 'ready' | 'failed' } = {
      0: 'processing',
      1: 'processing',
      2: 'processing',
      3: 'processing',
      4: 'ready',
      5: 'failed',
    };

    return {
      status: statusMap[videoData.status] || 'processing',
      progress: videoData.encodeProgress,
    };
  } catch (error) {
    console.error('Failed to get video status:', error);
    return { status: 'failed' };
  }
};

// ============================================
// LEGACY COMPATIBILITY FUNCTIONS
// These are aliases to maintain compatibility with existing code
// that uses Cloudinary function names
// ============================================

/**
 * @deprecated Use uploadToBunny instead
 * Legacy alias for compatibility with code that used Cloudinary
 */
export const uploadToCloudinary = uploadToBunny;

/**
 * @deprecated Use deleteFromBunny instead
 * Legacy alias for compatibility with code that used Cloudinary
 */
export const deleteFromCloudinary = deleteFromBunny;

/**
 * @deprecated Use validateBunnyUrl instead
 * Legacy alias for compatibility
 */
export const validateCloudinaryUrl = validateBunnyUrl;

/**
 * @deprecated Use isBunnyUrl instead
 * Legacy alias for compatibility
 */
export const isCloudinaryUrl = isBunnyUrl;